import { NewCard } from "@components/Cards/Templates/newCard";
import BusinessCardTable from "@components/Tables/BuisnessCardTable";
import { YobelyEditHistoryDropdown } from "@components/DropdownBars/YobelyEditHistoryDropdown/YobelyEditHistoryDropdown";
import { TextInputField } from "@components/Inputs/TextInputField/TextInputField";
import { UserFieldErrorMessages } from "@constants/Errors";
import messages from "@constants/Messages";
import {
  handleHiraganaKanaChange,
  handleNoValidationNameCheck,
  phoneNumberChange,
} from "@services/ErrorHandling";
import { USER } from "@types/user";

type ContactBasicInfoCardProps = {
  contactData: USER;
  loading: boolean;
  isViewingHistory: boolean;
  createContact: boolean;
  files: File[];
  setFiles: (files: File[]) => void;
  setBusinessCards: (cards: any[]) => void;
  setImageFileIndexes: (indexes: number[]) => void;
  contactHistoryData: any[];
  selectedOption: any;
  handleDropdownChange: (option: any) => void;
  updateUserData: (field: string, value: any) => void;
  updateError: (field: string, value: any) => void;
  errors: any;
};

export const ContactBasicInfoCard = ({
  contactData,
  loading,
  isViewingHistory,
  createContact,
  files,
  setFiles,
  setBusinessCards,
  setImageFileIndexes,
  contactHistoryData,
  selectedOption,
  handleDropdownChange,
  updateUserData,
  updateError,
  errors,
}: ContactBasicInfoCardProps) => {
  return (
    <NewCard
      header={"基本情報"}
      bodyLabel={"名刺画像"}
      loading={loading}
      bodyContents={
        <div>
          <div className="flex">
            <BusinessCardTable
              userId={contactData?.id}
              cardData={contactData?.card_image_urls || []}
              loading={loading}
              files={files}
              setFiles={setFiles}
              setData={setBusinessCards}
              setIndexToDelete={setImageFileIndexes}
              isCreate={createContact}
              disabled={isViewingHistory}
            />
          </div>

          <div className="divider mt-5 mb-6"></div>

          <h3 className="mt-10 mb-5 label-light-blue-xl">参照対象</h3>

          <YobelyEditHistoryDropdown
            selectedOption={selectedOption}
            contactHistoryData={contactHistoryData}
            handleDropdownChange={handleDropdownChange}
          />

          <div className="divider mt-5 mb-6"></div>
          <div>
            <div className="mt-10">
              <h2 className="mb-5 label-light-blue-xl">
                氏名{" "}
                <span className="text-base text-zinc-400 font-bold">
                  （姓・名いずれか必須）
                </span>{" "}
                <span className="text-red-500">*</span>
              </h2>
              <div className="flex flex-row gap-2 md:gap-9">
                <div className="flex flex-col">
                  <TextInputField
                    label={"姓"}
                    labelClassName="label-light-blue-md"
                    providedClassName="form-text-field-border-full"
                    value={contactData?.last_name}
                    placeholder={"山田"}
                    onChange={(e) =>
                      handleNoValidationNameCheck(
                        "last_name",
                        e.target.value,
                        updateUserData,
                        updateError,
                      )
                    }
                    disabled={isViewingHistory}
                  />
                  {errors.last_name && (
                    <p className="text-red-500">
                      {UserFieldErrorMessages.nameError}
                    </p>
                  )}
                </div>
                <div className="flex flex-col">
                  <TextInputField
                    label={"名"}
                    labelClassName="label-light-blue-md"
                    providedClassName="form-text-field-border-full"
                    value={contactData?.first_name}
                    placeholder={"太郎"}
                    onChange={(e) =>
                      handleNoValidationNameCheck(
                        "first_name",
                        e.target.value,
                        updateUserData,
                        updateError,
                      )
                    }
                    disabled={isViewingHistory}
                  />
                  {errors.first_name && (
                    <p className="text-red-500">
                      {UserFieldErrorMessages.nameError}
                    </p>
                  )}
                </div>
              </div>

              <div className="divider mt-5 mb-6"></div>

              <h2 className="mb-5 label-light-blue-xl">ふりがな</h2>
              <div className="flex flex-row gap-2 md:gap-9">
                <div className="flex flex-col">
                  <TextInputField
                    label={"せい"}
                    labelClassName="label-light-blue-md"
                    providedClassName="form-text-field-border-full"
                    placeholder="やまだ"
                    value={contactData?.last_furigana_name}
                    onChange={(e) => {
                      handleHiraganaKanaChange(
                        "last_furigana_name",
                        e.target.value,
                        updateUserData,
                        updateError,
                      );
                    }}
                    disabled={isViewingHistory}
                  />

                  {errors.last_furigana_name && (
                    <p className="text-red-500">
                      {UserFieldErrorMessages.nameHiraganaError}
                    </p>
                  )}
                </div>

                <div className="flex flex-col">
                  <TextInputField
                    label={"めい"}
                    labelClassName="label-light-blue-md"
                    providedClassName="form-text-field-border-full"
                    placeholder="たろう"
                    value={contactData?.first_furigana_name}
                    onChange={(e) => {
                      handleHiraganaKanaChange(
                        "first_furigana_name",
                        e.target.value,
                        updateUserData,
                        updateError,
                      );
                    }}
                    disabled={isViewingHistory}
                  />
                  {errors.first_furigana_name && (
                    <p className="text-red-500">
                      {UserFieldErrorMessages.nameHiraganaError}
                    </p>
                  )}
                </div>
              </div>

              <div className="divider mt-5 mb-6"></div>
              <div className="w-full md:w-3/5">
                <TextInputField
                  label={"メールアドレス"}
                  placeholder="yamada.taro@email.com"
                  providedClassName="form-text-field-border-full"
                  value={contactData?.email}
                  onChange={(e) =>
                    updateUserData("email", e.target.value.trim())
                  }
                  disabled={isViewingHistory}
                  type="email"
                />
                {errors.email && (
                  <p className="text-red-500">
                    {UserFieldErrorMessages.emailError}
                  </p>
                )}
              </div>

              <div className="divider mt-5 mb-6"></div>

              <div className="w-full md:w-3/5">
                <TextInputField
                  label={"個人電話番号 （ハイフンなし）"}
                  providedClassName="form-text-field-border-full"
                  value={contactData?.personal_tel}
                  placeholder="（例）08012345678"
                  onChange={(e) => {
                    phoneNumberChange(
                      "personal_tel",
                      e.target.value,
                      updateUserData,
                      updateError,
                    );
                  }}
                  type="tel"
                  disabled={isViewingHistory}
                />
                {errors.personal_tel && (
                  <p className="text-red-500">
                    {UserFieldErrorMessages.telephoneNumberError}
                  </p>
                )}
                <p className="text-mayo-light-gray">
                  {messages.user.personal_tel_or_company_tel_required}
                </p>
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
};
